import { FieldInstance, FieldTemplate } from "db/definitions";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import React, { useEffect } from "react";
import Svg from "../../Svg/Svg";
import OccupiedField from "../OccupiedField/OccupiedField";
import { NumberField, FieldWrapper, FieldActionWrapper, ConfirmationModal, ToastVariant } from "@factorearth/component-library";
import { useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { recordSentry } from "utils/recordSentry";

interface Props {
	/**
	 * The user's username
	 */
	authUsername: string;
	color: Colors;
	fieldInstance: FieldInstance;
	fieldTemplate: FieldTemplate;
	fromTable?: boolean;
	handleChange: (event: { target: { name: string; value: any } }) => Promise<void>;
	handleClickAndSubmit: (event: { target: { name: string; value: any } }) => Promise<void>;
	onBlur: (newValue?: string | number) => Promise<void>;
	/**
	 * The username of the person currently editing the field
	 */
	username: string;
	value: string;
	moreHorizon?: JSX.Element;
	charLimit?: number;
}
interface ToastContent {
	type: ToastVariant;
	title: string;
	message: string;
}
function Number(props: Props) {
	const {
		authUsername,
		color,
		fieldInstance,
		fieldTemplate,
		fromTable,
		handleChange,
		handleClickAndSubmit,
		onBlur,
		username,
		value,
		moreHorizon,
		charLimit,
	} = props;

	const maxLength = charLimit || 390000;
	const [currentValue, setCurrentValue] = useState(value);
	const { showBoundary } = useErrorBoundary();
	const [openToast, setOpenToast] = useState(false);
	const [toastContent, setToastContent] = useState<ToastContent>({
		type: "info",
		title: fieldTemplate.header,
		message: "",
	});

	useEffect(() => {
		setCurrentValue(value);
	}, [value]);

	const handleInputChange = async (e: {
		target: {
			name: string;
			value: string;
		};
	}) => {
		if (e.target.value.length >= maxLength) {
			setToastContent({
				type: "destructive",
				title: fieldTemplate.header,
				message: `You have reached the maximum amount of ${maxLength} characters allowed.`,
			});
			setOpenToast(true);
			recordSentry({ error: "You have reached the maximum amount of characters allowed.", msg: "You have reached the maximum characters allowed" });
		} else {
			// This is for fixing undo/redo issue
			handleChange(e);
			setCurrentValue(e.target.value);
		}
	};

	async function handleOnBlur() {
		try {
			await onBlur();
		} catch (error) {
			// Log only — do not call showBoundary. A save failure (e.g. analytics
			// error, network hiccup) must not crash the entire form component tree,
			// which would remount all cells and lose any other in-progress edits.
			recordSentry({ error, msg: "NumberField onBlur Failed" });
		}
	}

	return (
		<>
			<ConfirmationModal
				colorPalette={color}
				details={toastContent.message}
				confirmTitle={toastContent.title}
				open={openToast}
				setOpen={setOpenToast}
				toast={toastContent.type}
				timeout={5000}
				canClose
			/>
			<FieldWrapper
				colorPalette={color}
				label={!fromTable ? fieldTemplate.header : undefined}
				link={fieldInstance.altInstanceId ? <Svg icon="LINKED" /> : undefined}
				data-fieldtemplateid={fieldTemplate.id}
				data-test="Field-Header">
				{username && username !== authUsername ? (
					<OccupiedField username={username} moreHorizon={moreHorizon ? moreHorizon : undefined} />
				) : (
					<FieldActionWrapper data-test="input-wrap">
						<NumberField
							handleBlur={handleOnBlur}
							handleChange={handleInputChange}
							handleClickAndSubmit={handleClickAndSubmit}
							placeholder={fieldTemplate.placeholder || ""}
							value={currentValue}
							fromTable={fromTable}
							colorPalette={color}
							moreHorizon={moreHorizon ? moreHorizon : undefined}
							maxLength={charLimit}
						/>
					</FieldActionWrapper>
				)}
			</FieldWrapper>
		</>
	);
}
export default Number;
